import {ReactNode, memo, useEffect, useRef, useState, useCallback} from "react";
import {CheckCircle2, RefreshCw, Timer, X} from "lucide-react";

import {ConfirmModalProvider} from "@/Logigate/contexts/ConfirmModalConstest";
import {EchoProvider} from "@/components/WebSocketEcho";
import Countdown, {CountdownRenderProps} from "react-countdown";
import {toPad} from "@/utils/ForAll";
import {BotaoIcone, IconeTema} from "@/Logigate/ui";
import {router, usePage} from "@inertiajs/react";
import {CustomRequest} from "@/utils/CustomRequest";
import {EVENTO, publish} from "@/utils/events";
import ModalAutenticacao from "@/Logigate/ModalAutenticacao";
import ModalLockScreen from "@/Logigate/ModalLockScreen";
import {showPop} from "@/utils/Pops";
import {useMonitoramentoHeartbeat, useMonitoramentoNavigation} from "@/Logigate/hooks/monitoramento";
import {useExportNotification} from "@/Logigate/hooks/useExportNotification";
import {useBackfillNotification} from "@/Logigate/hooks/useBackfillNotification";
import {AgenteSilvaWidget} from "@/components/AgenteSilva";
import AvisoPrivacidadeMonitoramento from "@/Logigate/components/monitoramento/AvisoPrivacidadeMonitoramento";

interface Props {
    children: ReactNode,
}

/** O relógio do acesso temporário: dias (se os houver), horas, minutos e segundos. */
const renderer = ({hours, minutes, seconds, days, completed}: CountdownRenderProps) => {
    if (completed) {
        return (
            <>
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
                    <CheckCircle2 className="size-[18px]" aria-hidden/>
                </span>
                <span className="text-[13px] leading-snug text-zinc-700 dark:text-zinc-200">
                    Tempo esgotado. A página vai actualizar-se em instantes.
                </span>
            </>
        );
    }
    return (
        <>
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400">
                <Timer className="size-[18px]" aria-hidden/>
            </span>
            <span className="min-w-0 leading-tight">
                <span className="block text-[11px] font-medium text-red-600 dark:text-red-400">O teu acesso expira em</span>
                <span className="block font-mono text-sm font-semibold tabular-nums text-zinc-900 dark:text-zinc-50">
                    {days > 0 && `${days} d `}{toPad(hours, 2)}:{toPad(minutes, 2)}:{toPad(seconds, 2)}
                </span>
            </span>
        </>
    );
};

const CountdownWidget = memo(({tempo}: { tempo: any }) => (
    <div role="timer" className="pointer-events-auto flex max-w-full items-center gap-2.5 rounded-xl border border-zinc-200 bg-white/95 py-1.5 pl-1.5 pr-4 shadow-lg shadow-zinc-950/10 backdrop-blur dark:border-zinc-700 dark:bg-zinc-900/95">
        <Countdown zeroPadTime={2} zeroPadDays={2} date={tempo} renderer={renderer}/>
    </div>
));

/** «Acesso actualizado»: o tempo do acesso mudou. Some sozinho ao fim de 5 s. */
function AvisoAcessoActualizado({onFechar}: { onFechar: () => void }) {
    useEffect(() => {
        const t = window.setTimeout(onFechar, 5000);
        return () => window.clearTimeout(t);
    }, [onFechar]);

    return (
        <div role="status" className="pointer-events-auto flex w-full items-start gap-3 rounded-xl border border-zinc-200 bg-white p-3 shadow-xl shadow-zinc-950/10 animate-in fade-in-0 slide-in-from-bottom-2 md:w-[360px] dark:border-zinc-700 dark:bg-zinc-900">
            <IconeTema cor="blue" tamanho="sm">
                <RefreshCw/>
            </IconeTema>
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">Acesso actualizado</p>
                <p className="text-[13px] text-zinc-500 dark:text-zinc-400">O tempo de expiração do teu acesso foi alterado.</p>
            </div>
            <BotaoIcone rotulo="Fechar" onClick={onFechar} className="-mr-1 -mt-1 size-10 md:size-8">
                <X className="size-4"/>
            </BotaoIcone>
        </div>
    );
}

/**
 * O canto dos avisos flutuantes (monitoramento, acesso actualizado, tempo do acesso), uns por
 * cima dos outros. No telemóvel fica acima da barra de baixo do casco; no computador, à
 * esquerda do botão do Agente Silva. Abaixo da gaveta do menu (`z-50`) e das janelas.
 */
function CantoDosAvisos({children}: { children: ReactNode }) {
    return (
        <div className="pointer-events-none fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-[45] flex flex-col items-end gap-2 md:inset-x-auto md:bottom-5 md:right-24 print:hidden">
            {children}
        </div>
    );
}

function MainProvider({children}: Props) {
    return (
        <EchoProvider>
            <Conteudo children={children}/>
        </EchoProvider>
    );
}

const Conteudo = ({children}: { children: ReactNode }) => {

    const interval = useRef<any>(null);
    const running = useRef(false);
    const {props} = usePage<any>();
    const [open, setOpen] = useState(false);
    const [open2, setOpen2] = useState(false);
    // Conta as mudanças do tempo do acesso: cada uma volta a mostrar o aviso (0 = escondido).
    const [avisoAcesso, setAvisoAcesso] = useState(0);
    const fecharAvisoAcesso = useCallback(() => setAvisoAcesso(0), []);
    const [tempo, setTempo] = useState(props.acesso?.acesso_at);
    const tempoCurrent = useRef(props.acesso?.acesso_at);
    const [isLocked, setIsLocked] = useState(false);

    // ID do módulo de Monitoramento (definido em ModulosMap.php)
    const MODULO_MONITORAMENTO_ID = 13;

    // Integração do Monitoramento - activo apenas se empresa tem o módulo
    const empresaTemMonitoramento = props.sistema?.modulos?.includes(MODULO_MONITORAMENTO_ID) ?? false;
    const monitoramentoEnabled = !!props.auth?.user && !!props.base_path && empresaTemMonitoramento;

    // Debug: Log para verificar estado do monitoramento (apenas em desenvolvimento)
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('[Monitoramento] Estado:', {
                hasUser: !!props.auth?.user,
                hasBasePath: !!props.base_path,
                empresaTemMonitoramento,
                modulosEmpresa: props.sistema?.modulos,
                enabled: monitoramentoEnabled,
                basePath: props.base_path,
            });
        }
    }, [monitoramentoEnabled, props.auth?.user, props.base_path, empresaTemMonitoramento]);

    // Heartbeat para manter sessão activa (envia sinal a cada 30 segundos)
    useMonitoramentoHeartbeat({
        enabled: monitoramentoEnabled,
        intervaloMs: 30000,
        basePath: props.base_path as string,
        fonteCodigo: 'logigate',
    });

    // Tracking de navegação entre páginas
    useMonitoramentoNavigation({
        enabled: monitoramentoEnabled,
        basePath: props.base_path as string,
        fonteCodigo: 'logigate',
    });

    // Notificação em tempo real para exportações em segundo plano
    useExportNotification({
        channelName: `logigate.admin.${props.admin_id}`,
        enabled: !!props.admin_id,
        processosUrl: `${props.base_path}/processos`,
    });

    // Idem para a reconstrução da contabilidade, que corre durante bastante tempo
    useBackfillNotification({
        channelName: `logigate.admin.${props.admin_id}`,
        enabled: !!props.admin_id,
    });

    function reload() {
        // `total_mensagens` saiu das props partilhadas: era inicializada, transformada em
        // `""` e devolvida em todas as respostas do painel, e **nada** a calculava (A16).
        router.reload({
            only: ["acesso"],
        });
    }

    const verifica = async () => {
        if (props.auth.user && props.acesso) {
            const response = await CustomRequest(props.base_path + "/vs", {}, "GET");
            if (response.estado === "ok") {
                publish(EVENTO.NOTIFICA, response);
                if ((response.tipo_acesso !== props.acesso.tipo_acesso)) {
                    reload();
                } else {
                    if (response.acesso_at !== tempoCurrent.current) {
                        setTempo(response.acesso_at);
                        tempoCurrent.current = response.acesso_at;
                        setOpen2(true);
                    }
                }
            } else if (response.codigo === "EMPRESA_ENCERRADA") {
                // A empresa fechou (rescisão do contrato da escola) com o painel aberto: a página
                // inteira recarrega e mostra o ecrã de empresa fechada.
                window.location.reload();
            } else {
                if (response.auth) {
                    setOpen(true);
                }
            }
        }
        running.current = false;
    }

    useEffect(() => {
        interval.current = setInterval(() => {
            if (!running.current) {
                running.current = true;
                verifica();
            }
        }, 10000);

        return () => {
            if (interval.current !== null) {
                clearInterval(interval.current);
            }
        }
    }, []);

    // Quando o tempo do acesso muda, mostra o aviso «Acesso actualizado» no canto.
    useEffect(() => {
        if (open2) {
            setAvisoAcesso((n) => n + 1);
            setOpen2(false);
        }
    }, [open2]);

    // Função para bloquear e salvar estado no localStorage
    const handleLock = useCallback(async () => {
        const lock = localStorage.getItem("isLocked")
        if (lock === "true" && isLocked) return;
        if (props.no_modal) return;

        const url = props.base_path + "/tela-bloqueio/bloquear";
        const res: any = await CustomRequest(url, {}, "POST");
        if (res.estado === "ok") {
            setIsLocked(true);
            localStorage.setItem("isLocked", "true");
            localStorage.setItem("lockTimestamp", Date.now().toString());
        } else {
            showPop("", res.texto, "error")
        }
    }, [isLocked, props.no_modal, props.base_path]);

    // Função para desbloquear e sincronizar o estado com o localStorage
    const handleUnlock = () => {
        setIsLocked(false);
        localStorage.setItem("isLocked", "false");
    };

    // Escuta mudanças no `localStorage` entre janelas
    useEffect(() => {
        if (props.empresa) {
            const syncLockStatus = (event: StorageEvent) => {
                if (event.key === "isLocked") {
                    if (event.newValue === "true") {
                        setIsLocked(true);
                    } else {
                        setIsLocked(false);
                    }
                }
            };
            window.addEventListener("storage", syncLockStatus);
            return () => {
                window.removeEventListener("storage", syncLockStatus);
            };
        }
    }, []);

    // Configura o temporizador de inatividade e sincroniza entre janelas
    useEffect(() => {
        if (props.empresa && (window as any).b_estado === 1) {
            // Os temporizadores do browser são de 32 bits: acima de 2 147 483 647 ms o
            // `setTimeout` dispara logo, e o painel da empresa inteira bloqueava ao abrir
            // (geral-2 B07). O servidor já só aceita até 1 hora; isto é a segunda linha.
            const idleTimeLimit = Math.min(Math.max(Number((window as any).b_tempo) || 900000, 300000), 2147483647);
            let idleTimer = setTimeout(handleLock, idleTimeLimit);
            let lastReset = 0;
            const resetTimer = () => {
                const now = Date.now();
                if (now - lastReset < 1000) return;
                lastReset = now;
                clearTimeout(idleTimer);
                idleTimer = setTimeout(handleLock, idleTimeLimit);
            };
            // Num tablet não há rato nem teclado: rolar uma lista ou tocar no ecrã também é
            // actividade, senão o painel bloqueava a meio do trabalho (geral-2 B15).
            const eventos: Array<keyof WindowEventMap> = ["mousemove", "keydown", "pointerdown", "touchstart", "wheel", "scroll"];
            eventos.forEach((evento) => window.addEventListener(evento, resetTimer, {passive: true, capture: true}));
            return () => {
                clearTimeout(idleTimer);
                eventos.forEach((evento) => window.removeEventListener(evento, resetTimer, {capture: true}));
            };
        }
    }, [handleLock]);

    const onOk = () => {
        handleUnlock();
    }

    const empresaId = props.empresa?.id || 0;

    return (
        <ConfirmModalProvider>
            {open && <ModalAutenticacao open={open}/>}

            {isLocked && (
                <ModalLockScreen open={isLocked} onOk={onOk}/>
            )}

            {children}

            <CantoDosAvisos>
                {monitoramentoEnabled && empresaId > 0 && (
                    <AvisoPrivacidadeMonitoramento empresaId={empresaId}/>
                )}

                {avisoAcesso > 0 && (
                    <AvisoAcessoActualizado key={avisoAcesso} onFechar={fecharAvisoAcesso}/>
                )}

                {props.acesso?.acesso_at && props.auth?.user && (
                    <CountdownWidget tempo={tempo}/>
                )}
            </CantoDosAvisos>

            {/* Escondido (sem desmontar a conversa) enquanto o painel está bloqueado ou sem sessão:
                o botão dele fica acima de tudo e via-se por cima do ecrã de bloqueio. */}
            {props.auth?.user && empresaId > 0 && (
                <div className={isLocked || open ? "hidden" : "contents"}>
                    <AgenteSilvaWidget
                        modulo="logigate"
                        basePath={props.base_path as string}
                        entidadeId={empresaId}
                        entidadeNome={props.empresa?.nome}
                    />
                </div>
            )}
        </ConfirmModalProvider>
    );
}

export default MainProvider;
